import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AppLayout } from '../components/layout/AppLayout';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// Mirrors backend/src/routes/orderPayments.js PAYMENT_ROLES exactly.
const PAYMENT_ROLES = ['finance', 'rsm', 'sales_rep', 'bde', 'dispatch', 'admin', 'super_admin', 'ceo', 'director', 'vp_sales'];

const weekKey = d => {
  if (!d) return 'unknown';
  const dt = new Date(d);
  const jan1 = new Date(dt.getFullYear(), 0, 1);
  const wk = Math.ceil(((dt - jan1) / 86400000 + jan1.getDay() + 1) / 7);
  return `${dt.getFullYear()}-W${String(wk).padStart(2,'0')}`;
};
const weekLabel = k => {
  if (k === 'unknown') return '—';
  const [yr, wStr] = k.split('-W');
  const wn = parseInt(wStr, 10);
  const jan1 = new Date(Number(yr), 0, 1);
  const d = new Date(jan1.getTime() + (wn - 1) * 7 * 86400000);
  return `W${wn} ${d.toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}`;
};

const fmt = n => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 10000000) return `₹${(v/10000000).toFixed(2)}Cr`;
  if (Math.abs(v) >= 100000)   return `₹${(v/100000).toFixed(1)}L`;
  if (Math.abs(v) >= 1000)     return `₹${(v/1000).toFixed(0)}K`;
  return `₹${v.toLocaleString('en-IN')}`;
};
// Full precision, no K/L/Cr abbreviation — for per-order table rows, where
// a rounded "35K" hides whether it's really 34,500 or 35,200. Kept separate
// from fmt() above, which stays abbreviated for the KPI summary cards.
const fmtFull = n => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'2-digit' }) : '—';
const monthKey = d => d ? d.slice(0, 7) : 'unknown';   // YYYY-MM

// Quick date-range presets — shared by the default filter state and the
// quick-select buttons in the filter bar, so "This Month" always means the
// same thing whether it's the page's initial state or a button click.
const todayISO = () => new Date().toISOString().split('T')[0];
const isoDate = d => d.toISOString().split('T')[0];
const quickRangeStart = (kind) => {
  const now = new Date();
  if (kind === 'month') return new Date(now.getFullYear(), now.getMonth(), 1);
  if (kind === 'quarter') return new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
  // fy — Apr 1 of the current financial year
  const fyStart = new Date(now.getFullYear(), 3, 1);
  if (now < fyStart) fyStart.setFullYear(fyStart.getFullYear() - 1);
  return fyStart;
};
const QUICK_RANGES = [
  ['month',   'This Month'],
  ['quarter', 'This Quarter'],
  ['fy',      'This FY'],
];

// Defaults to the start of the current month — the FY-to-date range that
// used to load by default was too broad to be a useful starting view.
const defaultFrom = () => isoDate(quickRangeStart('month'));

export default function SalesLedgerPage() {
  const { user } = useAuth();
  const canRecordPayment = PAYMENT_ROLES.includes(user?.role_slug);
  const [actuals, setActuals] = useState([]);
  const [ytd, setYtd]         = useState([]);
  const [products, setProducts] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('analytics'); // analytics | lines | orders | ytd
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [payModal, setPayModal] = useState(null);
  const [ewayThreshold, setEwayThreshold] = useState(50000);
  const [groupBy, setGroupBy] = useState('product');   // product | territory | rep | bucket | month | customer_type
  const [filters, setFilters] = useState({
    status: 'approved',        // default to approved = real booked revenue
    product_id: '',
    territory_id: '',
    from: defaultFrom(),
    to: new Date().toISOString().split('T')[0],
  });

  // Masters once
  useEffect(() => {
    Promise.all([api.get('/products'), api.get('/territories')])
      .then(([p, t]) => { setProducts(p.data.data || []); setTerritories(t.data.data || []); })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 2000 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [a, y] = await Promise.all([
        api.get('/actuals', { params }),
        api.get('/actuals/ytd'),
      ]);
      setActuals(a.data.data || []);
      setYtd(y.data.data || []);
      if (a.data.eway_threshold != null) setEwayThreshold(Number(a.data.eway_threshold));
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const applyQuickRange = (kind) => setFilters(f => ({ ...f, from: isoDate(quickRangeStart(kind)), to: todayISO() }));

  // ── KPIs ────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const totalRevenue = actuals.reduce((s, a) => s + Number(a.revenue || 0), 0);
    const totalUnits   = actuals.reduce((s, a) => s + Number(a.units || 0), 0);
    const pendingApprovals = actuals.filter(a => a.status === 'pending').length;
    const pricePending = actuals.filter(a => a.price_approval_status === 'pending_approval').length;
    const soIds = new Set(actuals.map(a => a.sales_order_id).filter(Boolean));
    const doIds = new Set(actuals.map(a => a.distributor_order_id).filter(Boolean));
    const orderCount = soIds.size + doIds.size + actuals.filter(a => !a.sales_order_id && !a.distributor_order_id).length;
    // Weighted discount
    const listTotal = actuals.reduce((s, a) => s + Number(a.list_price || a.unit_price || 0) * Number(a.units || 0), 0);
    const revTotal  = actuals.reduce((s, a) => s + Number(a.revenue || 0), 0);
    const avgDiscount = listTotal > 0 ? ((listTotal - revTotal) / listTotal) * 100 : 0;
    return { totalRevenue, totalUnits, orderCount, avgDiscount, pendingApprovals, pricePending };
  }, [actuals]);

  // ── Group-by aggregation ────────────────────────────────────────
  const grouped = useMemo(() => {
    const map = new Map();
    actuals.forEach(a => {
      let key, label;
      switch (groupBy) {
        case 'territory':     key = a.territory_id || 'none';        label = a.territory_name || '— No territory'; break;
        case 'rep':           key = a.logged_by    || 'none';        label = a.logged_by_name || '—'; break;
        case 'bucket':        key = a.bucket       || 'unknown';     label = a.bucket || 'Unknown'; break;
        case 'month':         key = monthKey(a.order_date);          label = key === 'unknown' ? '—' : new Date(key + '-01').toLocaleDateString('en-IN', { month:'short', year:'2-digit' }); break;
        case 'customer_type': key = a.doctor_id ? 'doctor' : (a.sales_order_id ? 'order' : 'legacy'); label = key; break;
        default:              key = a.product_id || 'none';          label = a.product_name || '—';
      }
      if (!map.has(key)) map.set(key, { key, label, revenue: 0, units: 0, lines: 0 });
      const g = map.get(key);
      g.revenue += Number(a.revenue || 0);
      g.units   += Number(a.units || 0);
      g.lines   += 1;
    });
    const arr = [...map.values()].sort((a, b) => b.revenue - a.revenue);
    const max = arr[0]?.revenue || 1;
    return arr.map(r => ({ ...r, pct: (r.revenue / max) * 100, share: kpis.totalRevenue > 0 ? (r.revenue / kpis.totalRevenue) * 100 : 0 }));
  }, [actuals, groupBy, kpis.totalRevenue]);

  // ── Monthly trend ────────────────────────────────────────────────
  const trend = useMemo(() => {
    const map = new Map();
    actuals.forEach(a => {
      const k = monthKey(a.order_date);
      if (!map.has(k)) map.set(k, { key: k, revenue: 0 });
      map.get(k).revenue += Number(a.revenue || 0);
    });
    const arr = [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
    const max = Math.max(...arr.map(r => r.revenue), 1);
    return arr.map(r => ({ ...r, pct: (r.revenue / max) * 100,
      label: r.key === 'unknown' ? '—' : new Date(r.key + '-01').toLocaleDateString('en-IN', { month:'short' }) }));
  }, [actuals]);

  // ── Weekly trend ─────────────────────────────────────────────────
  const trendWeek = useMemo(() => {
    const map = new Map();
    actuals.forEach(a => {
      const k = weekKey(a.order_date);
      if (!map.has(k)) map.set(k, { key: k, revenue: 0, orders: new Set() });
      const g = map.get(k);
      g.revenue += Number(a.revenue || 0);
      const oid = a.sales_order_id || a.distributor_order_id;
      if (oid) g.orders.add(oid);
    });
    const arr = [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
    const max = Math.max(...arr.map(r => r.revenue), 1);
    return arr.map(r => ({ ...r, pct: (r.revenue / max) * 100, label: weekLabel(r.key), orderCount: r.orders.size }));
  }, [actuals]);

  // ── By-order grouping ────────────────────────────────────────────
  // Payment total-due is GST-inclusive — sales_orders.total_order_value /
  // distributor_orders.total_selling_value are both stored GST-exclusive
  // pre-invoice (same root cause fixed in SaleForm.jsx/DistributorOrderForm.jsx
  // OrderPaymentsPanel wiring). For SO, actuals already stores per-line
  // igst/cgst/sgst amounts — sum those. For DO, mirror the same
  // ptd/selling + gst-on-base formula FinancialSummaryPanel already uses.
  const byOrders = useMemo(() => {
    const map = new Map();
    actuals.forEach(a => {
      const key  = a.sales_order_id || a.distributor_order_id || `legacy-${a.id}`;
      const type = a.sales_order_id ? 'SO' : a.distributor_order_id ? 'DO' : 'LEG';
      const num  = a.sales_order_number || a.distributor_order_number || '—';
      if (!map.has(key)) {
        map.set(key, {
          key, type, num,
          orderId:   a.sales_order_id || a.distributor_order_id,
          date:      a.order_date,
          customer:  a.customer_name || a.doctor_name || '—',
          territory: a.territory_name || '—',
          rep:       a.logged_by_name || '—',
          status:    a.status,
          orderStatus: type === 'SO' ? a.so_order_status : type === 'DO' ? a.do_order_status : null,
          invoiceAmount: type === 'SO' ? Number(a.so_invoice_amount || 0) : Number(a.do_invoice_amount || 0),
          amountPaid: type === 'SO' ? Number(a.so_amount_paid || 0) : Number(a.do_amount_paid || 0),
          // Every addon on the order (shipping, integration fee, extended
          // warranty, etc.) with its own real GST already included — see
          // the order_addon_line_items LATERAL join. so_shipping_charges/
          // do_shipping_charges is a separate, apparently-unused legacy
          // column (confirmed live), deliberately not read here anymore.
          addonsTotal: Number(a.order_addons_total || 0),
          doTotalPtd: Number(a.do_total_ptd_value || 0),
          doTotalSell: Number(a.do_total_selling_value || 0),
          doTotalGst: Number(a.do_total_gst_value || 0),
          doNsrOverride: !!a.do_nsr_override,
          // The REAL linked Books invoice(s) — never trust so_invoice_amount/
          // do_invoice_amount alone for "is this actually invoiced" (Ship
          // Gate Audit: those fields can be set by paths with nothing real
          // behind them). An order can have MULTIPLE invoices now (partial
          // invoicing), so these are aggregates across all of them, not one
          // row — linkedInvoiceCount=0 means no invoices row is linked at
          // all; count>0 with neither any_confirmed nor any_draft means
          // every linked invoice is VOID (a real, distinct state — not the
          // same as "Draft", which the badge used to collapse it into).
          linkedInvoiceCount:      Number(a.linked_invoice_count || 0),
          linkedInvoiceAnyConfirmed: !!a.linked_invoice_any_confirmed,
          linkedInvoiceAnyDraft:   !!a.linked_invoice_any_draft,
          linkedInvoiceNumber:     a.linked_invoice_number || null,
          linkedInvoiceTotal:      a.linked_invoice_total != null ? Number(a.linked_invoice_total) : null,
          linkedInvoiceAmountPaid: a.linked_invoice_amount_paid != null ? Number(a.linked_invoice_amount_paid) : null,
          linkedInvoiceEwayNumber: a.linked_invoice_eway_number || null,
          linkedInvoiceEwayExempt: a.linked_invoice_eway_exempt || null,
          // "Needs collection" — a confirmed invoice on this order is past
          // its due_date with an outstanding balance (flag, not a block —
          // see Actuals overdue-flagging decision). Never true for an order
          // with no confirmed invoice.
          linkedInvoiceAnyOverdue: !!a.linked_invoice_any_overdue,
          linkedInvoiceEarliestOverdueDate: a.linked_invoice_earliest_overdue_date || null,
          revenue: 0, units: 0, gstAmt: 0, lines: [],
        });
      }
      const g = map.get(key);
      g.revenue += Number(a.revenue || 0);
      g.units   += Number(a.units   || 0);
      // actuals.igst_amount/cgst_amount/sgst_amount are only populated for
      // ~20% of live Sales Order lines — the main SO creation/edit paths
      // (salesOrdersController.js) never write them at all, so summing
      // those columns alone silently dropped tax for most orders (confirmed
      // live: MI-SO-26-0009 and the majority of SO actuals rows). Fall back
      // to the product master's own gst_rate against this line's revenue
      // when the real per-line amount is zero/missing — an estimate, but a
      // much closer one than treating untaxed lines as genuinely tax-free.
      const realLineGst = Number(a.igst_amount || 0) + Number(a.cgst_amount || 0) + Number(a.sgst_amount || 0);
      g.gstAmt += realLineGst > 0 ? realLineGst : Number(a.revenue || 0) * (Number(a.product_gst_rate || 0) / 100);
      g.lines.push(a);
    });
    return [...map.values()].map(o => {
      // Prefer the real linked invoice(s)' own totals (true GST-inclusive
      // figures, summed straight from the invoices) — fall back to an
      // estimate, WITH shipping charges added in (previously dropped
      // entirely from every fallback branch), only when no real invoice
      // has been generated yet.
      let totalDue, amountPaidReal, balanceDue;
      if (o.linkedInvoiceCount > 0) {
        totalDue = o.linkedInvoiceTotal ?? 0;
        amountPaidReal = o.linkedInvoiceAmountPaid ?? 0;
        balanceDue = totalDue - amountPaidReal;
      } else if (o.invoiceAmount > 0) {
        totalDue = o.invoiceAmount + o.addonsTotal; // legacy-invoiced, no real invoices row (see Ship Gate Audit grandfather note)
        amountPaidReal = o.amountPaid;
        balanceDue = totalDue - amountPaidReal;
      } else if (o.type === 'SO') {
        totalDue = o.revenue + o.gstAmt + o.addonsTotal;
        amountPaidReal = o.amountPaid;
        balanceDue = totalDue - amountPaidReal;
      } else if (o.type === 'DO') {
        const gstBase = o.doNsrOverride ? o.doTotalSell : o.doTotalPtd;
        const gstOnBase = o.doTotalPtd > 0 ? gstBase * (o.doTotalGst / o.doTotalPtd) : 0;
        totalDue = gstBase + gstOnBase + o.addonsTotal;
        amountPaidReal = o.amountPaid;
        balanceDue = totalDue - amountPaidReal;
      } else {
        totalDue = o.revenue;
        amountPaidReal = o.amountPaid;
        balanceDue = totalDue - amountPaidReal;
      }
      const paymentStatus = o.orderStatus === 'settled' ? 'settled'
        : amountPaidReal > 0 ? 'received'
        : 'pending';
      const invoiceConfirmed = o.linkedInvoiceAnyConfirmed;
      // Visible, not silently cleaned up (Ledger Feedback Review decision):
      // the order's own lifecycle status claims it's invoiced, but nothing
      // real backs that up — either no invoice was ever linked, or every
      // linked invoice is VOID. Surfaced so it can be reviewed/decided
      // per-order rather than guessed at here.
      const statusInvoiceMismatch = ['invoiced','shipped','dispatched','delivered','payment_received','partially_paid','settled'].includes(o.orderStatus)
        && !invoiceConfirmed;
      const ewayNeeded = totalDue > ewayThreshold;
      // Flag, don't block (Actuals overdue-flagging decision): balanceDue>0
      // is re-checked here even though the backend already requires it,
      // since balanceDue here can differ slightly from the invoice-only
      // balance_due when legacy/estimate fallback totals are in play.
      const needsCollection = o.linkedInvoiceAnyOverdue && balanceDue > 0;
      return {
        ...o, totalDue, amountPaid: amountPaidReal, balanceDue, paymentStatus,
        invoiceConfirmed, statusInvoiceMismatch, ewayNeeded, needsCollection,
        ewayStatus: !ewayNeeded ? 'not_needed'
          : o.linkedInvoiceEwayNumber ? 'attached'
          : o.linkedInvoiceEwayExempt ? 'exempt'
          : 'missing',
      };
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [actuals, ewayThreshold]);

  const toggleOrder = id => setExpandedOrders(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (
    <AppLayout title="Sales Ledger" subtitle="Line-item view of booked revenue, approvals and variance">
      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 14 }}>
        {[['analytics','Analytics'], ['orders','By Order'], ['lines','Line Items'], ['ytd','YTD vs Target']].map(([k, l]) =>
          <button key={k} className={`tab-btn${tab === k ? ' active' : ''}`} onClick={() => setTab(k)}>{l}</button>
        )}
      </div>

      {/* Shared filter bar for analytics + lines */}
      {tab !== 'ytd' && (
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', marginBottom:14 }}>
          <div style={{ display:'flex', gap:4 }}>
            {QUICK_RANGES.map(([k, l]) => {
              const active = filters.from === isoDate(quickRangeStart(k)) && filters.to === todayISO();
              return (
                <button key={k} onClick={() => applyQuickRange(k)}
                  style={{ padding:'6px 12px', fontSize:'0.78rem', fontWeight:600, borderRadius:6, cursor:'pointer',
                           border:'1px solid var(--border)',
                           background: active ? 'var(--brand-purple)' : 'transparent',
                           color: active ? '#fff' : 'var(--text-secondary)' }}>
                  {l}
                </button>
              );
            })}
          </div>
          <input type="date" className="form-control" style={{ width:150 }} value={filters.from} onChange={e=>setFilter('from', e.target.value)}/>
          <span style={{ color:'var(--text-muted)' }}>→</span>
          <input type="date" className="form-control" style={{ width:150 }} value={filters.to} onChange={e=>setFilter('to', e.target.value)}/>
          <select className="form-control" style={{ width:170 }} value={filters.territory_id} onChange={e=>setFilter('territory_id', e.target.value)}>
            <option value="">All territories</option>
            {territories.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select className="form-control" style={{ width:180 }} value={filters.product_id} onChange={e=>setFilter('product_id', e.target.value)}>
            <option value="">All products</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select className="form-control" style={{ width:140 }} value={filters.status} onChange={e=>setFilter('status', e.target.value)}>
            <option value="">All statuses</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
          <button className="btn btn-ghost btn-sm" onClick={load}>↻ Refresh</button>
          <Link to="/sales-orders/new" className="btn btn-primary btn-sm" style={{ marginLeft:'auto' }}>+ New Sales Order</Link>
        </div>
      )}

      {tab === 'orders' && (
        <>
          {/* Summary stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:12, marginBottom:16 }}>
            <KPI label="Orders"     value={byOrders.length.toLocaleString()}                        sub="Unique SO / DO" />
            <KPI label="SO"         value={byOrders.filter(o=>o.type==='SO').length.toLocaleString()} sub="Sales orders" />
            <KPI label="DO"         value={byOrders.filter(o=>o.type==='DO').length.toLocaleString()} sub="Distributor orders" />
            <KPI label="Total (incl. tax)" value={fmtFull(byOrders.reduce((s,o)=>s+o.totalDue,0))}         sub="Total booked" />
            <KPI label="Collected"  value={fmtFull(byOrders.reduce((s,o)=>s+o.amountPaid,0))}            sub="Total received" color="#059669" />
            <KPI label="Pending"    value={fmtFull(byOrders.reduce((s,o)=>s+Math.max(o.balanceDue,0),0))} sub="Outstanding" color="#B45309" />
            <KPI label="Overdue"    value={byOrders.filter(o=>o.needsCollection).length.toLocaleString()}
                 sub={fmtFull(byOrders.filter(o=>o.needsCollection).reduce((s,o)=>s+Math.max(o.balanceDue,0),0)) + ' needs collection'}
                 color="#991B1B" />
          </div>

          <Card>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', fontSize:'0.83rem', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ color:'var(--text-muted)', fontSize:'0.72rem', textTransform:'uppercase', letterSpacing:'0.05em', borderBottom:'1px solid var(--border)' }}>
                    <th style={{ ...th, width:30 }}></th>
                    <th style={th}>Type</th>
                    <th style={th}>Order #</th>
                    <th style={th}>Date</th>
                    <th style={th}>Customer</th>
                    <th style={th}>Territory</th>
                    <th style={th}>Rep</th>
                    <th style={{ ...th, textAlign:'right' }}>Units</th>
                    <th style={{ ...th, textAlign:'right' }}>Total (incl. tax)</th>
                    <th style={{ ...th, textAlign:'right' }}>Collected</th>
                    <th style={{ ...th, textAlign:'right' }}>Pending</th>
                    <th style={th}>Invoice</th>
                    <th style={th}>Lifecycle</th>
                    <th style={th}>E-way</th>
                    <th style={th}>Status</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={16} style={{ padding:30, textAlign:'center', color:'var(--text-muted)' }}>Loading…</td></tr>
                  )}
                  {!loading && byOrders.length === 0 && (
                    <tr><td colSpan={16} style={{ padding:30, textAlign:'center', color:'var(--text-muted)' }}>No orders match these filters.</td></tr>
                  )}
                  {byOrders.map(o => {
                    const expanded = expandedOrders.has(o.key);
                    const isSO = o.type === 'SO';
                    const isDO = o.type === 'DO';
                    const badgeColor  = isSO ? '#0D9488' : isDO ? '#7C3AED' : '#6B7280';
                    const statusColor = o.status === 'approved' ? '#059669' : o.status === 'rejected' ? '#DC2626' : '#B45309';
                    return (
                      <Fragment key={o.key}>
                        <tr onClick={() => toggleOrder(o.key)}
                            style={{ borderTop:'1px solid var(--border)', cursor:'pointer',
                                     background: expanded ? 'var(--bg-hover, rgba(0,0,0,0.03))' : undefined }}>
                          <td style={{ padding:'8px 6px', textAlign:'center', color:'var(--text-muted)', fontWeight:700 }}>
                            {expanded ? '▼' : '▶'}
                          </td>
                          <td style={{ padding:'8px 10px' }}>
                            <span style={{ background: badgeColor, color:'#fff', borderRadius:4,
                                           padding:'2px 7px', fontSize:'0.7rem', fontWeight:700 }}>
                              {o.type}
                            </span>
                          </td>
                          <td style={{ padding:'8px 10px' }}>
                            {o.orderId
                              ? <Link to={isSO ? `/sales-orders/${o.orderId}` : `/distributor-orders/${o.orderId}`}
                                      style={{ color:'var(--brand-purple)', fontWeight:600 }}
                                      onClick={e => e.stopPropagation()}>
                                  {o.num}
                                </Link>
                              : <span style={{ color:'var(--text-muted)' }}>{o.num}</span>
                            }
                          </td>
                          <td style={{ padding:'8px 10px', whiteSpace:'nowrap' }}>{fmtDate(o.date)}</td>
                          <td style={{ padding:'8px 10px', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{o.customer}</td>
                          <td style={{ padding:'8px 10px' }}>{o.territory}</td>
                          <td style={{ padding:'8px 10px' }}>{o.rep}</td>
                          <td style={{ padding:'8px 10px', textAlign:'right' }}>{o.units.toLocaleString()}</td>
                          <td style={{ padding:'8px 10px', textAlign:'right', fontWeight:700 }}>{fmtFull(o.totalDue)}</td>
                          <td style={{ padding:'8px 10px', textAlign:'right', color:'#059669', fontWeight:600 }}>{fmtFull(o.amountPaid)}</td>
                          <td style={{ padding:'8px 10px', textAlign:'right', color: o.balanceDue > 0 ? '#B45309' : '#9CA3AF', fontWeight:600 }}>
                            {fmtFull(Math.max(o.balanceDue, 0))}
                            {o.needsCollection && (
                              <div title={`Confirmed invoice past due since ${fmtDate(o.linkedInvoiceEarliestOverdueDate)} with an outstanding balance. Flagged for collection follow-up — not blocked.`}
                                style={{ marginTop:3, fontSize:'0.68rem', fontWeight:700, padding:'1px 6px', borderRadius:4,
                                         background:'#DC2626', color:'#fff', display:'inline-flex', alignItems:'center', gap:3, cursor:'help', whiteSpace:'nowrap' }}>
                                ⚠ Overdue since {fmtDate(o.linkedInvoiceEarliestOverdueDate)}
                              </div>
                            )}
                          </td>
                          <td style={{ padding:'8px 10px' }}>
                            {o.type === 'LEG' ? '—' : o.linkedInvoiceCount === 0 ? (
                              <span style={{ fontSize:'0.72rem', fontWeight:700, padding:'2px 7px', borderRadius:4, background:'#F3F4F6', color:'#6B7280' }}>No</span>
                            ) : o.invoiceConfirmed ? (
                              <span title={o.linkedInvoiceNumber || ''}
                                style={{ fontSize:'0.72rem', fontWeight:700, padding:'2px 7px', borderRadius:4, background:'#D1FAE5', color:'#065F46' }}>
                                ✓ Yes{o.linkedInvoiceCount > 1 ? ` (${o.linkedInvoiceCount})` : ''}
                              </span>
                            ) : o.linkedInvoiceAnyDraft ? (
                              <span title={o.linkedInvoiceNumber || ''} style={{ fontSize:'0.72rem', fontWeight:700, padding:'2px 7px', borderRadius:4, background:'#FEF3C7', color:'#92400E' }}>Draft</span>
                            ) : (
                              <span title={o.linkedInvoiceNumber || ''} style={{ fontSize:'0.72rem', fontWeight:700, padding:'2px 7px', borderRadius:4, background:'#FEE2E2', color:'#991B1B' }}>Voided</span>
                            )}
                          </td>
                          <td style={{ padding:'8px 10px' }}>
                            {o.orderStatus && (
                              <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:'0.76rem', color:'#374151', textTransform:'capitalize' }}>
                                {o.orderStatus.replace(/_/g,' ')}
                                {o.statusInvoiceMismatch && (
                                  <span title={`This order's status says "${o.orderStatus}", but it has ${o.linkedInvoiceCount === 0 ? 'no linked invoice' : 'no confirmed invoice — only voided/draft ones'}. Flagged for review, not auto-corrected.`}
                                    style={{ fontSize:'0.7rem', color:'#991B1B', cursor:'help' }}>⚠</span>
                                )}
                              </span>
                            )}
                          </td>
                          <td style={{ padding:'8px 10px' }}>
                            {o.type !== 'LEG' && o.ewayStatus !== 'not_needed' && (
                              <span style={{ fontSize:'0.72rem', fontWeight:700, padding:'2px 7px', borderRadius:4,
                                       background: o.ewayStatus === 'attached' ? '#D1FAE5' : o.ewayStatus === 'exempt' ? '#F3F4F6' : '#FEE2E2',
                                       color: o.ewayStatus === 'attached' ? '#065F46' : o.ewayStatus === 'exempt' ? '#6B7280' : '#991B1B' }}>
                                {o.ewayStatus === 'attached' ? '✓ Attached' : o.ewayStatus === 'exempt' ? 'Exempt' : '⚠ Missing'}
                              </span>
                            )}
                            {o.ewayStatus === 'not_needed' && <span style={{ fontSize:'0.72rem', color:'#9CA3AF' }}>—</span>}
                          </td>
                          <td style={{ padding:'8px 10px' }}>
                            <span style={{ color: statusColor, fontWeight:600, textTransform:'capitalize' }}>{o.status || '—'}</span>
                          </td>
                          <td style={{ padding:'8px 10px' }} onClick={e => e.stopPropagation()}>
                            {canRecordPayment && o.type !== 'LEG' && o.orderStatus && o.paymentStatus !== 'settled' && (
                              <button onClick={() => setPayModal(o)}
                                style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #0D9488', background:'#F0FDFA', color:'#0D9488', fontWeight:700, fontSize:'0.72rem', cursor:'pointer', whiteSpace:'nowrap' }}>
                                💰 Record Payment
                              </button>
                            )}
                          </td>
                        </tr>
                        {expanded && o.lines.map((line, idx) => {
                          const discPct = line.discount_pct != null
                            ? Number(line.discount_pct)
                            : (line.list_price && line.unit_price
                               ? ((Number(line.list_price) - Number(line.unit_price)) / Number(line.list_price)) * 100
                               : null);
                          return (
                            <tr key={`${o.key}-line-${idx}`}
                                style={{ background:'var(--bg-hover, rgba(0,0,0,0.025))', borderTop:'1px solid var(--border)', fontSize:'0.78rem' }}>
                              <td style={{ padding:'5px 6px' }}></td>
                              <td style={{ padding:'5px 10px', color:'var(--text-muted)' }}>└</td>
                              <td colSpan={2} style={{ padding:'5px 10px', color:'var(--text-muted)', fontStyle:'italic' }}>
                                {line.product_name || '—'}
                              </td>
                              <td style={{ padding:'5px 10px', color:'var(--text-muted)' }}>{line.bucket || '—'}</td>
                              <td style={{ padding:'5px 10px' }}></td>
                              <td style={{ padding:'5px 10px' }}></td>
                              <td style={{ padding:'5px 10px', textAlign:'right' }}>{Number(line.units||0).toLocaleString()}</td>
                              <td style={{ padding:'5px 10px', textAlign:'right', fontWeight:600 }} title="Line revenue (pre-tax) — the order row above shows the real GST-inclusive total">{fmtFull(Number(line.revenue||0))}</td>
                              <td style={{ padding:'5px 10px' }}></td>
                              <td style={{ padding:'5px 10px' }}></td>
                              <td style={{ padding:'5px 10px' }}></td>
                              <td style={{ padding:'5px 10px' }}></td>
                              <td style={{ padding:'5px 10px' }}></td>
                              <td style={{ padding:'5px 10px', color:'var(--text-muted)', fontSize:'0.72rem' }}>
                                {discPct != null ? `${discPct.toFixed(1)}% off` : ''}
                              </td>
                              <td></td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {payModal && (
        <QuickPaymentModal order={payModal} onClose={() => setPayModal(null)} onSaved={() => { setPayModal(null); load(); }} />
      )}

      {tab === 'analytics' && (
        <>
          {/* KPI tiles */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:12, marginBottom:16 }}>
            <KPI label="Revenue"            value={fmtFull(kpis.totalRevenue)}              sub={`${actuals.length} lines`} />
            <KPI label="Orders"             value={kpis.orderCount.toLocaleString()}    sub="Unique orders" />
            <KPI label="Units"              value={kpis.totalUnits.toLocaleString()}    sub="Units sold" />
            <KPI label="Avg discount"       value={kpis.avgDiscount > 0 ? `${kpis.avgDiscount.toFixed(1)}%` : '—'}
                 sub="Vs list price" color={kpis.avgDiscount > 15 ? '#B91C1C' : kpis.avgDiscount > 5 ? '#B45309' : '#059669'}/>
            <KPI label="Pending approvals"  value={`${kpis.pendingApprovals}`}
                 sub={kpis.pricePending > 0 ? `${kpis.pricePending} price-variance` : 'Sale approvals'}
                 color={kpis.pendingApprovals > 0 ? '#B45309' : 'inherit'}/>
          </div>

          {/* Weekly + Monthly trend */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            <Card title="Revenue by week">
              {trendWeek.length === 0 ? <Empty/> : (
                <div style={{ overflowX:'auto' }}>
                  <div style={{ display:'flex', alignItems:'flex-end', gap:4, height:140, padding:'4px 0 6px', minWidth: trendWeek.length * 36 }}>
                    {trendWeek.map(t => (
                      <div key={t.key} style={{ flex:'0 0 32px', display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                        <div style={{ fontSize:'0.6rem', color:'var(--text-muted)', marginBottom:2, fontWeight:600 }}>{fmt(t.revenue)}</div>
                        <div title={`${t.label}: ${fmt(t.revenue)} · ${t.orderCount} orders`}
                             style={{ width:'100%', background:'#0D9488', borderRadius:3,
                                     height:`${Math.max(t.pct, 3)}%`, transition:'height 0.4s', cursor:'default' }}/>
                        <div style={{ fontSize:'0.6rem', color:'var(--text-muted)', textAlign:'center', lineHeight:1.2 }}>{t.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            <Card title="Revenue by month">
              {trend.length === 0 ? <Empty/> : (
                <div style={{ display:'flex', alignItems:'flex-end', gap:6, height:140, padding:'4px 0 6px' }}>
                  {trend.map(t => (
                    <div key={t.key} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                      <div title={fmt(t.revenue)} style={{ width:'100%', background:'var(--brand-purple)', borderRadius:3,
                            height:`${Math.max(t.pct, 3)}%`, transition:'height 0.4s' }}/>
                      <div style={{ fontSize:'0.65rem', color:'var(--text-muted)' }}>{t.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <Card title="Top contributors" action={
              <select className="form-control" style={{ width:160, padding:'4px 8px', fontSize:'0.8rem' }}
                value={groupBy} onChange={e=>setGroupBy(e.target.value)}>
                <option value="product">By product</option>
                <option value="territory">By territory</option>
                <option value="rep">By rep</option>
                <option value="bucket">By bucket</option>
                <option value="month">By month</option>
                <option value="customer_type">By customer type</option>
              </select>
            }>
              {grouped.length === 0 ? <Empty/> : (
                <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:250, overflowY:'auto' }}>
                  {grouped.slice(0, 12).map(g => (
                    <div key={g.key} style={{ display:'flex', alignItems:'center', gap:10, fontSize:'0.82rem' }}>
                      <div style={{ flex:'0 0 130px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.label}</div>
                      <div style={{ flex:1, height:18, background:'var(--bg-card)', borderRadius:4, overflow:'hidden' }}>
                        <div style={{ background:'var(--brand-purple)', height:'100%', width:`${g.pct}%`, transition:'width 0.4s' }}/>
                      </div>
                      <div style={{ flex:'0 0 90px', textAlign:'right', fontWeight:600 }}>{fmt(g.revenue)}</div>
                      <div style={{ flex:'0 0 50px', textAlign:'right', fontSize:'0.72rem', color:'var(--text-muted)' }}>{g.share.toFixed(1)}%</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

          {/* Full grouped table */}
          <Card title={`All ${groupBy.replace('_',' ')} breakdown`}>
            <table style={{ width:'100%', fontSize:'0.85rem', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ color:'var(--text-muted)', fontSize:'0.72rem', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  <th style={th}>{groupBy.replace('_',' ')}</th>
                  <th style={{ ...th, textAlign:'right' }}>Lines</th>
                  <th style={{ ...th, textAlign:'right' }}>Units</th>
                  <th style={{ ...th, textAlign:'right' }}>Revenue</th>
                  <th style={{ ...th, textAlign:'right' }}>Share</th>
                </tr>
              </thead>
              <tbody>
                {grouped.length === 0 && <tr><td colSpan={5} style={{ padding:20, textAlign:'center', color:'var(--text-muted)' }}>No rows match these filters.</td></tr>}
                {grouped.map(g => (
                  <tr key={g.key} style={{ borderTop:'1px solid var(--border)' }}>
                    <td style={{ padding:'8px 10px', fontWeight:600 }}>{g.label}</td>
                    <td style={{ padding:'8px 10px', textAlign:'right' }}>{g.lines}</td>
                    <td style={{ padding:'8px 10px', textAlign:'right' }}>{g.units.toLocaleString()}</td>
                    <td style={{ padding:'8px 10px', textAlign:'right', fontWeight:600 }}>{fmt(g.revenue)}</td>
                    <td style={{ padding:'8px 10px', textAlign:'right', color:'var(--text-muted)' }}>{g.share.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {tab === 'lines' && (
        <Card>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', fontSize:'0.82rem', borderCollapse:'collapse' }}>
              <thead style={{ background:'var(--bg-card)' }}>
                <tr>
                  {['Date','Order','Customer','Product','Bucket','Units','List','Unit','Revenue','Discount','Territory','Rep','Status','Pricing'].map(h =>
                    <th key={h} style={{ ...th, padding:'8px 10px' }}>{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={14} style={{ padding:30, textAlign:'center', color:'var(--text-muted)' }}>Loading…</td></tr>}
                {!loading && actuals.length === 0 && <tr><td colSpan={14} style={{ padding:30, textAlign:'center', color:'var(--text-muted)' }}>No line items match these filters.</td></tr>}
                {actuals.map(a => {
                  const pct = a.discount_pct != null ? Number(a.discount_pct) : (a.list_price && a.unit_price
                    ? ((Number(a.list_price) - Number(a.unit_price)) / Number(a.list_price)) * 100 : null);
                  return (
                    <tr key={a.id} style={{ borderTop:'1px solid var(--border)' }}>
                      <td style={{ padding:'8px 10px', whiteSpace:'nowrap' }}>{fmtDate(a.order_date)}</td>
                      <td style={{ padding:'8px 10px' }}>
                        {a.sales_order_id
                          ? <Link to={`/sales-orders/${a.sales_order_id}`} style={{ color:'var(--brand-purple)', fontWeight:600 }}>{a.sales_order_number || 'SO'}</Link>
                          : a.distributor_order_id
                            ? <Link to={`/distributor-orders/${a.distributor_order_id}`} style={{ color:'#0D9488', fontWeight:600 }}>{a.distributor_order_number || 'DO'}</Link>
                            : <span style={{ color:'var(--text-muted)' }}>— (legacy)</span>}
                      </td>
                      <td style={{ padding:'8px 10px' }}>{a.customer_name || a.doctor_name || '—'}</td>
                      <td style={{ padding:'8px 10px', fontWeight:500 }}>{a.product_name}</td>
                      <td style={{ padding:'8px 10px', fontSize:'0.72rem', color:'var(--text-muted)' }}>{a.bucket}</td>
                      <td style={{ padding:'8px 10px', textAlign:'right' }}>{a.units}</td>
                      <td style={{ padding:'8px 10px', textAlign:'right', color:'var(--text-muted)' }}>{a.list_price ? fmt(a.list_price) : '—'}</td>
                      <td style={{ padding:'8px 10px', textAlign:'right' }}>{fmt(a.unit_price)}</td>
                      <td style={{ padding:'8px 10px', textAlign:'right', fontWeight:600 }}>{fmt(a.revenue)}</td>
                      <td style={{ padding:'8px 10px', textAlign:'right', color: pct == null ? 'var(--text-muted)' : pct > 0 ? '#059669' : pct < 0 ? '#7C3AED' : 'var(--text-muted)' }}>
                        {pct == null ? '—' : `${pct > 0 ? '-' : '+'}${Math.abs(pct).toFixed(1)}%`}
                      </td>
                      <td style={{ padding:'8px 10px' }}>{a.territory_name || '—'}</td>
                      <td style={{ padding:'8px 10px', fontSize:'0.75rem', color:'var(--text-muted)' }}>{a.logged_by_name}</td>
                      <td style={{ padding:'8px 10px' }}><StatusBadge status={a.status}/></td>
                      <td style={{ padding:'8px 10px' }}>
                        {a.price_approval_status && a.price_approval_status !== 'within_tier'
                          ? <StatusBadge status={a.price_approval_status}/>
                          : <span style={{ fontSize:'0.7rem', color:'var(--text-muted)' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === 'ytd' && (
        <Card>
          <table style={{ width:'100%', fontSize:'0.85rem', borderCollapse:'collapse' }}>
            <thead style={{ background:'var(--bg-card)' }}>
              <tr>
                {['Territory','Product','Bucket','Target Units','Actual Units','Target Revenue','Actual Revenue','Gap','Achievement'].map(h =>
                  <th key={h} style={{ ...th, padding:'8px 10px' }}>{h}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {ytd.length === 0 && <tr><td colSpan={9} style={{ padding:30, textAlign:'center', color:'var(--text-muted)' }}>No data yet</td></tr>}
              {ytd.map((r, i) => (
                <tr key={i} style={{ borderTop:'1px solid var(--border)' }}>
                  <td style={{ padding:'8px 10px', fontWeight:600 }}>{r.territory}</td>
                  <td style={{ padding:'8px 10px' }}>{r.product}</td>
                  <td style={{ padding:'8px 10px' }}><span className="badge badge-purple" style={{ fontSize:'0.68rem' }}>{r.bucket}</span></td>
                  <td style={{ padding:'8px 10px' }}>{Number(r.target_units||0).toLocaleString()}</td>
                  <td style={{ padding:'8px 10px', fontWeight:600 }}>{Number(r.actual_units||0).toLocaleString()}</td>
                  <td style={{ padding:'8px 10px', color:'var(--text-secondary)' }}>{fmt(r.target_revenue)}</td>
                  <td style={{ padding:'8px 10px', fontWeight:600 }}>{fmt(r.actual_revenue)}</td>
                  <td style={{ padding:'8px 10px', color: r.revenue_gap > 0 ? 'var(--danger)' : 'var(--success)', fontWeight:600 }}>
                    {r.revenue_gap > 0 ? `-${fmt(r.revenue_gap)}` : '✓'}
                  </td>
                  <td style={{ padding:'8px 10px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <div className="progress-bar" style={{ width:60 }}>
                        <div className={`progress-bar-fill ${r.achievement_pct>=80?'success':r.achievement_pct>=50?'':'warning'}`}
                          style={{ width:`${Math.min(r.achievement_pct||0, 100)}%` }}/>
                      </div>
                      <span style={{ fontSize:'0.78rem', fontWeight:600 }}>{r.achievement_pct||0}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </AppLayout>
  );
}

const th = { padding: '10px 12px', textAlign: 'left', fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' };

function Card({ title, action, children }) {
  return (
    <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:10, padding:14, marginBottom:14 }}>
      {(title || action) && (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
          {title && <h3 style={{ margin:0, fontSize:'0.88rem', fontWeight:700 }}>{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function KPI({ label, value, sub, color }) {
  return (
    <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px' }}>
      <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</div>
      <div style={{ fontSize:'1.3rem', fontWeight:700, marginTop:4, color: color || 'inherit' }}>{value}</div>
      {sub && <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:2 }}>{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    approved:         { bg:'#D1FAE5', fg:'#065F46' },
    pending:          { bg:'#FEF3C7', fg:'#92400E' },
    rejected:         { bg:'#FEE2E2', fg:'#991B1B' },
    within_tier:      { bg:'#E0E7FF', fg:'#3730A3' },
    pending_approval: { bg:'#FEF3C7', fg:'#92400E' },
  }[status] || { bg:'#F3F4F6', fg:'#6B7280' };
  return <span style={{ padding:'2px 8px', borderRadius:99, background:styles.bg, color:styles.fg, fontSize:'0.7rem', fontWeight:600 }}>{status?.replace('_',' ')}</span>;
}

function Empty() {
  return <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', fontSize:'0.85rem' }}>Nothing in this range.</div>;
}

// Lightweight record-payment modal for the Sales Ledger quick action —
// same fields/endpoint as OrderPaymentsPanel (frontend/src/components/orders),
// just without the running history list since this page isn't a single-order view.
function QuickPaymentModal({ order, onClose, onSaved }) {
  const idParam = order.type === 'SO' ? 'sales_order_id' : 'distributor_order_id';
  const invoiced = !!order.orderStatus && !['draft','pending_approval','approved','revision_requested','rejected','qc_in_progress','qc_done','qc_pending','dispatched','shipped','confirmed'].includes(order.orderStatus);
  const [form, setForm] = useState({ amount: '', payment_type: invoiced ? 'balance' : 'advance', payment_mode: 'bank_transfer', reference_number: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const inputStyle = { width:'100%', padding:'8px 10px', borderRadius:8, border:'1px solid #D1D5DB', fontSize:'0.85rem', fontFamily:'inherit', boxSizing:'border-box' };
  const labelStyle = { display:'block', fontSize:'0.75rem', fontWeight:600, color:'#374151', marginBottom:4 };

  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setSaving(true);
    try {
      await api.post('/order-payments', { [idParam]: order.orderId, ...form, amount: amt });
      toast.success(`${form.payment_type[0].toUpperCase()}${form.payment_type.slice(1)} recorded`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to record payment');
    } finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(15,14,42,0.5)', backdropFilter:'blur(3px)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:12, width:420, maxWidth:'100%', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid #E5E7EB' }}>
          <div style={{ fontWeight:700, fontSize:'1rem' }}>💰 Record Payment — {order.num}</div>
          <div style={{ fontSize:'0.75rem', color:'#6B7280', marginTop:2 }}>
            {order.customer} · Due {fmt(Math.max(order.balanceDue, 0))}
          </div>
        </div>
        <div style={{ padding:20, display:'flex', flexDirection:'column', gap:12 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <label style={labelStyle}>Type</label>
              <select style={inputStyle} value={form.payment_type} onChange={e => setForm(f => ({ ...f, payment_type: e.target.value }))}>
                {!invoiced && <option value="advance">Advance</option>}
                {invoiced && <option value="balance">Balance</option>}
                {order.orderStatus === 'cancelled' && <option value="refund">Refund</option>}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Amount (₹)</label>
              <input type="number" style={inputStyle} value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <label style={labelStyle}>Mode</label>
              <select style={inputStyle} value={form.payment_mode} onChange={e => setForm(f => ({ ...f, payment_mode: e.target.value }))}>
                {['bank_transfer','cheque','upi','cash','neft','rtgs','other'].map(m => <option key={m} value={m}>{m.replace('_',' ')}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Reference Number</label>
              <input style={inputStyle} value={form.reference_number} onChange={e => setForm(f => ({ ...f, reference_number: e.target.value }))} placeholder="UTR / cheque no / UPI ref" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Notes (optional)</label>
            <input style={inputStyle} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
        <div style={{ padding:'14px 20px', borderTop:'1px solid #E5E7EB', display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose} style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #D1D5DB', background:'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:'0.83rem' }}>Cancel</button>
          <button onClick={submit} disabled={saving} style={{ padding:'8px 20px', borderRadius:8, border:'none', background:'#0D9488', color:'#fff', fontWeight:700, cursor:'pointer', fontFamily:'inherit', fontSize:'0.83rem', opacity:saving?0.7:1 }}>
            {saving ? 'Saving…' : 'Save Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}
